import type { ReploSimpleProject } from "schemas/generated/project";
import type { UserRole } from "schemas/generated/user";

import * as React from "react";
import { useState } from "react";

import LogoBadge from "@common/LogoBadge";
import Avatar from "@editor/components/common/designSystem/Avatar";
import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { getProjectName } from "@editor/utils/project-utils";

import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  FolderSearch,
  PencilRuler,
  Plus,
  Search,
  Settings,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

const ShopMenu = ({
  selectedWorkspace,
  onShopClick,
}: {
  selectedWorkspace: {
    workspaceId: string;
    workspaceName: string;
    currentUserRole: UserRole;
    projects: ReploSimpleProject[];
  };
  onShopClick: (
    shopId: string,
    hostingType: "replo" | "shopify",
    shopName: string | undefined | null,
  ) => void;
}) => {
  const { openModal } = useModal();
  const projectId = useCurrentProjectId();
  const [searchQuery, setSearchQuery] = useState("");

  // Reset search query when workspace changes
  useOnValueChange(selectedWorkspace.workspaceId, () => {
    setSearchQuery("");
  });

  const hasMultipleShops = selectedWorkspace.projects.length > 1;

  // Filter projects based on search query
  const filteredProjects = React.useMemo(() => {
    if (!searchQuery) {
      return selectedWorkspace.projects;
    }

    const query = searchQuery.toLowerCase();
    return selectedWorkspace.projects.filter((project) =>
      getProjectName(project).toLowerCase().includes(query),
    );
  }, [selectedWorkspace.projects, searchQuery]);

  const showEmptyState =
    searchQuery.length > 0 && filteredProjects.length === 0;

  return (
    <div className="flex flex-col h-full gap-1.5">
      {hasMultipleShops && (
        <div className="px-2 pt-2">
          <DebouncedInput
            size="sm"
            placeholder="Search"
            value={searchQuery}
            onValueChange={setSearchQuery}
            startEnhancer={<Search size={12} />}
            layoutClassName="min-h-6"
            autoFocus
          />
        </div>
      )}

      <div className={hasMultipleShops ? "px-2" : "px-2 pt-2"}>
        <MenuItem
          variant="custom"
          size="base"
          UNSAFE_className="header-h5 hover:bg-transparent cursor-default"
          endEnhancer={
            selectedWorkspace.projects.length === 0 ? (
              <Link to={`/workspace/${selectedWorkspace.workspaceId}/settings`}>
                <Settings size={16} className="cursor-pointer" />
              </Link>
            ) : undefined
          }
        >
          {selectedWorkspace.workspaceName}
        </MenuItem>
      </div>

      {showEmptyState ? (
        <div className="flex flex-1 justify-center items-center p-4">
          <EmptyState
            title={`No results matching "${searchQuery}"`}
            description="Try a different search"
            size="sm"
            variant="primary"
            icon={<FolderSearch size={16} />}
          />
        </div>
      ) : (
        <div className="flex-1 px-2 pr-0 overflow-y-auto">
          <div className="flex flex-col gap-0.5 pr-1">
            <MenuItem
              variant="default"
              size="base"
              selected={false}
              onClick={() => {
                openModal({
                  type: "createShopFlowModal",
                  props: {
                    workspaceId: selectedWorkspace.workspaceId,
                  },
                });
              }}
              startEnhancer={
                <div className="border-border-component bg-sheet flex h-5 w-5 items-center justify-center rounded-[5px] border border-dashed">
                  <Plus
                    size={10}
                    className="text-muted"
                    absoluteStrokeWidth
                    strokeWidth={1.25}
                  />
                </div>
              }
              data-testid={`new-shop-${selectedWorkspace.workspaceId}`}
            >
              New Shop
            </MenuItem>

            {filteredProjects.map((workspaceProject) => (
              <MenuItem
                key={workspaceProject.id}
                variant="default"
                size="base"
                onClick={() => {
                  onShopClick(
                    workspaceProject.id,
                    workspaceProject.hostingType,
                    workspaceProject.name,
                  );
                }}
                selected={workspaceProject.id === projectId}
                startEnhancer={
                  <Avatar
                    icon={
                      workspaceProject.hostingType === "replo" ? (
                        <LogoBadge className="h-5 w-5 cursor-pointer text-black" />
                      ) : (
                        <PencilRuler size={10} className="text-muted" />
                      )
                    }
                    isSquare
                    size="sm"
                    className="py-0.5 rounded-md items-center w-5 h-5"
                    imageClassName={twMerge(
                      "w-5 h-5 bg-transparent",
                      workspaceProject.hostingType !== "replo" &&
                        "text-primary border-0.5 border-menu-item-selected bg-sheet",
                    )}
                  />
                }
              >
                {getProjectName(workspaceProject)}
              </MenuItem>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopMenu;
